import React, { useState, useEffect } from 'react';
import {
  Typography,
  Popper,
  List,
  RadioGroup,
  ListSubheader,
  FormControl,
  FormControlLabel,
  Grid,
  Theme,
  ListItem,
  ClickAwayListener,
  ToggleButton,
} from '@mui/material';
import { Tooltip } from '@globalvisioninc/ui/tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { getInspectionId, inspection, getSelectedModule, getDifferenceSortOption, getSortByModule } from 'store';
import { CTF_MODULE_TO_DIFFERENCE_MODULE, SortDifferencesOption, CTFModuleTypes } from 'types';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import GVRadio from 'components/lib/GVRadio/GVRadio';
import { updateDifferenceViewOptions } from 'store/request/inspections/actions';
import { makeStyles } from 'tss-react/mui';
import { getStyleVariables } from 'styles/vars';

const SINGLE_TYPE_MODULES: CTFModuleTypes[] = [
  CTFModuleTypes.graphics,
  CTFModuleTypes.barcode,
  CTFModuleTypes.braille,
  CTFModuleTypes.color,
  CTFModuleTypes.markup,
];

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    orderingPopper: {
      zIndex: 1,
    },
    subheader: {
      padding: theme.spacing(2, 2, 0, 2),
    },
    subheaderTitle: {
      textTransform: 'uppercase',
      color: 'var(--gds-color-text-supportive)',
    },
    orderingList: {
      width: 185,
      border: `solid 1px ${theme.palette.action.disabled}`,
      borderRadius: '4px',
      boxShadow:
        'rgb(0 0 0 / 20%) 0px 3px 3px -2px, rgb(0 0 0 / 14%) 0px 3px 4px 0px, rgb(0 0 0 / 12%) 0px 1px 8px 0px',
      backgroundColor: styleVariables.colors.menuLightGrey,
    },
    icon: {
      width: theme.spacing(2),
      height: theme.spacing(2),
      cursor: 'pointer',
    },
    toggleButton: {
      borderRadius: '50%',
      minWidth: 0,
      padding: theme.spacing(0.5),
      margin: theme.spacing(0.25),
      color: 'rgba(255,255,255,.7)',
      border: 'none',
      '&:hover': {
        color: 'rgba(255,255,255)',
      },
    },
  };
});

const OrderingList = () => {
  const { classes } = useStyles();

  const [openSort, setOpenSort] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const inspectionId = useSelector(getInspectionId);
  const selectedModule = useSelector(getSelectedModule);
  // Effective sort already accounts for the per-Spelling alphabetical preference.
  const sortOptionValue = useSelector(getDifferenceSortOption);
  const sortByPerModule = useSelector(getSortByModule);
  const showDifferenceTypeSort = !SINGLE_TYPE_MODULES.includes(selectedModule as CTFModuleTypes);
  const dispatch = useDispatch();

  // If the module no longer supports Difference Type sort, reset to Reading Order for that module.
  // This covers all navigation paths (tab click, auto-select, etc.).
  useEffect(() => {
    if (!showDifferenceTypeSort && sortOptionValue === SortDifferencesOption.differenceType) {
      if (selectedModule === 'All') {
        dispatch(inspection.actions.setDifferenceViewOptions({ sortBy: SortDifferencesOption.readingOrder }));
        dispatch(
          updateDifferenceViewOptions({ sortOptions: { sortBy: SortDifferencesOption.readingOrder } }, inspectionId),
        );
      } else {
        const moduleKey = CTF_MODULE_TO_DIFFERENCE_MODULE[selectedModule];
        const nextSortByPerModule = {
          ...sortByPerModule,
          [moduleKey]: SortDifferencesOption.readingOrder,
        };
        dispatch(
          inspection.actions.setDifferenceViewOptions({
            sortByPerModule: { [moduleKey]: SortDifferencesOption.readingOrder },
          }),
        );
        dispatch(updateDifferenceViewOptions({ sortOptions: { sortByPerModule: nextSortByPerModule } }, inspectionId));
      }
    }
  }, [showDifferenceTypeSort, sortOptionValue, selectedModule, sortByPerModule, dispatch, inspectionId]);

  const handleToggleSort = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setOpenSort(!openSort);
    setAnchorEl(event.currentTarget);
  };
  const handleCloseSort = () => {
    setOpenSort(false);
  };

  const handleChoose = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSortValue = event.currentTarget.value as SortDifferencesOption;

    if (selectedModule === 'All') {
      dispatch(inspection.actions.setDifferenceViewOptions({ sortBy: newSortValue }));
      dispatch(updateDifferenceViewOptions({ sortOptions: { sortBy: newSortValue } }, inspectionId));
    } else {
      const moduleKey = CTF_MODULE_TO_DIFFERENCE_MODULE[selectedModule];
      const nextSortByPerModule = {
        ...sortByPerModule,
        [moduleKey]: newSortValue,
      };
      dispatch(inspection.actions.setDifferenceViewOptions({ sortByPerModule: { [moduleKey]: newSortValue } }));
      dispatch(updateDifferenceViewOptions({ sortOptions: { sortByPerModule: nextSortByPerModule } }, inspectionId));
    }

    setOpenSort(false);
  };

  return (
    <>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <ToggleButton
            value="orderingButton"
            selected={openSort}
            className={classes.toggleButton}
            onClick={handleToggleSort}
            disableRipple
          >
            <ImportExportIcon className={classes.icon} />
          </ToggleButton>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="bottom">Sort</Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
      {
        <Popper
          open={openSort}
          anchorEl={anchorEl}
          className={classes.orderingPopper}
          placement="bottom-end"
          id="orderingPopper"
          modifiers={[
            { name: 'flip', enabled: false },
            { name: 'preventOverflow', enabled: false, options: { boundariesElement: 'scrollParent' } },
            { name: 'hide', enabled: false },
          ]}
        >
          <ClickAwayListener onClickAway={handleCloseSort}>
            <List
              className={classes.orderingList}
              dense
              subheader={
                <ListSubheader disableSticky disableGutters className={classes.subheader}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="body1" className={classes.subheaderTitle}>
                        Sort Differences By
                      </Typography>
                    </Grid>
                  </Grid>
                </ListSubheader>
              }
            >
              <ListItem className={classes.subheader}>
                <FormControl component="fieldset">
                  <RadioGroup
                    id="inspectionSortDifferencesOption"
                    value={sortOptionValue}
                    name="inspectionSortDifferencesOption"
                    onChange={handleChoose}
                  >
                    <FormControlLabel
                      value={SortDifferencesOption.readingOrder}
                      id={SortDifferencesOption.readingOrder}
                      control={<GVRadio color="secondary" />}
                      label="Reading Order"
                    />
                    {showDifferenceTypeSort && (
                      <FormControlLabel
                        value={SortDifferencesOption.differenceType}
                        id={SortDifferencesOption.differenceType}
                        control={<GVRadio color="secondary" />}
                        label="Difference Type"
                      />
                    )}
                    <FormControlLabel
                      value={SortDifferencesOption.differenceStatus}
                      id={SortDifferencesOption.differenceStatus}
                      control={<GVRadio color="secondary" />}
                      label="Status"
                    />
                    {selectedModule === CTFModuleTypes.fontMetrics && (
                      <FormControlLabel
                        value={SortDifferencesOption.fontSize}
                        id={SortDifferencesOption.fontSize}
                        control={<GVRadio color="secondary" />}
                        label="Font Size"
                      />
                    )}
                    {selectedModule === CTFModuleTypes.spelling && (
                      <FormControlLabel
                        value={SortDifferencesOption.alphabetical}
                        id={SortDifferencesOption.alphabetical}
                        control={<GVRadio color="secondary" />}
                        label="Alphabetical Order"
                      />
                    )}
                  </RadioGroup>
                </FormControl>
              </ListItem>
            </List>
          </ClickAwayListener>
        </Popper>
      }
    </>
  );
};

export default OrderingList;
